<template>
  <div class="fts-sidenav-module">
    <template v-for="(x, ind) in filterMenu(menus)">
      <!-- module (group, menu) -->
      <div 
        class="module-wrapper module-group" 
        :class="{ open: x.open }" 
        :style="moduleChild" 
        :key="x.id" 
        @click="openChildren(x)" 
        v-if="isGroup(x)"
      >
        <div class="module-info">
          <!-- emoji -->
          <fts-moji 
            :size="14" 
            :emoji="x.emoji" 
            class="module-emoji" 
            v-if="!!x.emoji"
          />
          <!-- name -->
          <fts-tippy
            trigger-class="module-name"
            :options="{ placement: 'top-start' }"
            :content="x.name"
            class="module-name-wrapper"
          >
            <template #trigger>{{x.name}}</template>
          </fts-tippy>
          <!-- profile -->
          <div class="module-tag" v-if="x.isProfile">
            <font-awesome-icon :icon="['fas', 'user']" fixed-width />
          </div>
          <!-- Module parent tile with child -->
          <div class="module-action-container" v-if="isDesignMode && x.module">
            <font-awesome-icon 
              :icon="['fas', 'ellipsis-vertical']" 
              class="module-action" 
              @click.stop.prevent="openMiniStudio(x.id)"
            />

            <font-awesome-icon 
              :icon="['fas', 'plus']" 
              class="module-action" 
              @click.stop.prevent="toggleView(
                openMiniStudio(x.id),
                { new: true }
              )" 
            />
          </div>
        </div>
      </div>
      <!-- custom (script) -->
      <div
        class="module-wrapper"
        :style="moduleChild"
        :key="x.id"
        @click="runScript(x)"
        v-else-if="x.script"
      >
        <div class="module-info">
          <!-- emoji -->
          <fts-moji 
            :size="14" 
            :emoji="x.emoji" 
            class="module-emoji" 
            v-if="!!x.emoji"
          />
          <!-- name -->
          <fts-tippy
            trigger-class="module-name"
            :options="{ placement: 'top-start' }"
            :content="x.name"
            class="module-name-wrapper"
          >
            <template #trigger>{{x.name}}</template>
          </fts-tippy>
        </div>
      </div>
      <!-- module / menu -->
      <router-link 
        :to="listViewRoute(x.id)" 
        custom 
        v-slot="{ navigate, href, route }" 
        class="module-wrapper" 
        :class="[
          {'module-wrapper--admin': x.id == 'everything'},
          {'active': isActive(x.id)}
        ]"
        :style="moduleChild"
        :key="x.id"
        v-else
      >
        <a :href="href" @click.prevent="openListView(navigate, route)">
          <div class="module-info">
            <template v-if="x.id == 'everything'">
              <fts-moji 
                :size="14" 
                :emoji="x.emoji" 
                class="module-emoji" 
                v-if="!!x.emoji"
              />
              <fts-tippy
                trigger-class="module-name"
                :options="{ placement: 'top-start' }"
                content="For admin only"
                class="module-name-wrapper"
                v-if="x.id == 'everything'"
              >
                <template #trigger>{{x.name}}</template>
              </fts-tippy>
            </template>

            <template v-else>
              <fts-moji 
                :size="14" 
                :emoji="x.emoji" 
                class="module-emoji" 
                v-if="!!x.emoji"
              />
              <fts-tippy
                trigger-class="module-name"
                :options="{ placement: 'top-start' }"
                :content="x.name"
                class="module-name-wrapper"
              >
                <template #trigger>{{x.name}}</template>
              </fts-tippy>
            </template>

            <div class="module-tag" v-if="x.isProfile">
              <font-awesome-icon :icon="['fas', 'user']" fixed-width />
            </div>
            <!-- module without listing / listing -->
            <div class="module-action-container" v-if="isDesignMode">
              <font-awesome-icon 
                :icon="['fas','ellipsis-vertical']" 
                class="module-action" 
                @click.stop.prevent="x.module? openMiniStudio(x.id) : toggleView(
                  openListView(navigate, route), 
                  { listingId: x.id }
                )"
              />
              <font-awesome-icon 
                :icon="['fas','plus']" 
                class="module-action" 
                @click.stop.prevent="toggleView(
                  openListView(navigate, route), 
                  { new: true }
                )" 
              />
            </div>
          </div>
        </a>
      </router-link>
      <!-- child -->
      <fts-sidenav-module
        :mode="mode"
        child
        :modules="x.children"
        :deep="deep + 1"
        :parent-id="getParentId(x)"
        :is-design-mode="isDesignMode"
        v-if="x.open"
        :key="`${x.id || ind}-child`"
      />
    </template>
  </div>
</template>

<script>
import { authorizationStore } from '../store/authorizationStore';
import { clientStore } from '../store/clientStore';
import commons from '../commons/commons';
import { SYSTEM_CAPABILITY, isCapableOf } from '../commons/flags';

import FTSBtn from '../elements/FTSBtn';
import FTSContenteditable from '../elements/FTSContenteditable';
import FTSEmoji from '../elements/FTSEmoji';
import FTSMoji from '../elements/FTSMoji';
import FTSReleaseBadge from '../elements/FTSReleaseBadge';
import FTSTippy from '../elements/FTSTippy';

export default {
  name: "fts-sidenav-module",
  props: {
    mode: String,
    modules: Array,
    deep: {
      type: Number,
      default: 0,
    },
    child: Boolean,
    parentId: [String, Number],
    action: Boolean,
    filter: String,
    isDesignMode: Boolean
  },
  components: {
    "fts-btn": FTSBtn,
    "fts-contenteditable": FTSContenteditable,
    "fts-emoji": FTSEmoji,
    "fts-moji": FTSMoji,
    "fts-release-badge": FTSReleaseBadge,
    "fts-tippy": FTSTippy
  },
  data: () => ({}),
  methods: {
    isCreatable(moduleId) {
      const modules = clientStore.state.workspaceModules;
      const module = modules.find(x => x.id == moduleId);
      return isCapableOf(SYSTEM_CAPABILITY.Write, modules, moduleId) && module && module.canCreate;
    },
    isGroup(x) {
      return (
        x.type === "group" ||
        (x.children && x.children.length && x.children[0].default !== true)
      );
    },
    isActive(itemId) {
      const currentTarget = this.parentId ? `${this.parentId}-${itemId}` : `${itemId}`;
      const target = this.list.id ? `${this.module.id}-${this.list.id}` : `${this.module.id}`;
      return currentTarget == target;
    },
    filterMenu(items) {
      let vm = this;

      if(!vm.filter || vm.filter == '') {
        return items;
      }

      return items.filter(x => vm.filterMenuInternal(x, vm)); 
    },
    filterMenuInternal(item, vm) {
      if(item.name) {
        if(item.name.toLowerCase().indexOf(vm.filter) > -1)
            return true;
      }
      else {
        console.log(item);
      }

      if(item.children) {
        return item.children.some((x) => vm.filterMenuInternal(x, vm.filter));
      }

      return false;
    },
    openChildren(x) {
      if (commons.elementStopPropagation()) {
        return;
      } else if (x.open === undefined) {
        this.$set(x, "open", true);
      } else {
        x.open = !x.open;
      }
    },
    runScript(x) {
      eval(x.script);
    },
    getParentId(x) {
      if (x.type !== "group") return x.id;
    },
    listViewRoute(x) {
      if (this.mode === "design")
        return;
      else if (this.parentId)
        return {
          name: 'lite',
          params: { moduleId: commons.encodeids(this.parentId) },
          query: { l: x }
        }
      else
        return {
          name: 'lite',
          params: { moduleId: commons.encodeids(x) }
        }
    },
    designRoute(moduleId, listId) {
      let route = {
        name: 'studio',
        query: { id: commons.encodeids(moduleId) }
      }

      if (listId)
        route.query = {
          ...route.query,
          t: 'menu',
          tid: listId
        }

      return route;
    },
    openListView(navigate, route) {
      if (commons.elementStopPropagation()) {
        return;
      } else {
        navigate(route);
      }
    },
    openCreateForm(item) {
      if (this.$ftsDetailView) {
        window.eventBus.$emit("detailView:form", {
          moduleId: item.id,
          listingId: Array.isArray(item.children) && item.children[0] && item.children[0].id,
          activityId: "create"
        });
      } else {
        window.eventBus.$emit("quickViewData", {
          activityId: "create",
          vectorId: item.id,
        });
      }
    },
    openListDesign(x) {
      if (this.mode == "design") {
        this.$delete(x, "hover");
        this.$emit("list", x.id);
      }
    },
    routeTo(route) { 
      if (route) {
        this.$router.push(route);
      } 
    },
    async toggleView(func, args = {}){ 
      await func; 
      window.eventBus.$emit("open-designer", args); 
    },
    openMiniStudio(moduleId) {
      window.eventBus.$emit("design.mini-studio-open", { moduleId: moduleId });
    }
  },
  computed: {
    isAdminUser() {
      return authorizationStore.getters.isAdminUser;
    },
    menus: {
      get() {
        var result =  this.child ? this.modules : commons.visibleMenu(clientStore.state.workspace.menus);
        return result.filter(m => m.id !== 'everything');
      },
      set(val) {
        if (!this.child) {
          clientStore.dispatch("setWorkspaceMenus", val);
        }
      }
    },
    moduleChild() {
      return this.child && { 'padding-left': `${(this.deep * 20) + 8}px` };
    },
    module() {
      return clientStore.state.module || {};
    },
    moduleId() {
      return clientStore.getters.moduleId;
    },
    list() {
      return clientStore.state.list || {};
    }
  }
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars.scss" as *;

.fts-sidenav-module {
  .module-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    margin-left: 0.375rem;
    margin-right: 0.375rem;
    margin-bottom: 0.125rem;
    padding: 0.375rem 0.75rem;
    border-radius: 0.25rem;
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-label-text-14--line-height);
    text-decoration: unset;
    color: var(--inistate-text-primary-color);
    cursor: pointer;
    .module-info {
      min-width: 0;
      flex: 1;
      display: flex;
      align-items: center;
      cursor: inherit;
      .module-emoji {
        margin-right: 0.375rem;
        cursor: inherit;
      }
      .module-name-wrapper {
        min-width: 0;
        cursor: inherit;
        .module-name {
          @include text-overflow-ellipsis;
          cursor: inherit;
        }
      }
      .module-contenteditable {
        margin-right: 0.5rem;
      }
      .module-tag {
        margin-left: 0.375rem;
        padding: 0.1rem 0.125rem;
        font-size: 0.625rem;
        border-radius: 0.25rem;
        background-color: var(--drawer-tag-background, var(--drawer-secondary-background));
        color: var(--drawer-tag, var(--drawer-secondary));
      }
    }
    .module-action-container {
      position: absolute;
      right: 6px;
      display: none;
      .module-action {
        height: 12px;
        width: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 4px;
        border-radius: var(--inistate-rounded-4);
        box-shadow: var(--fts-elevation-1);
        background-color: var(--fts-gray-25);
        color: var(--fts-default);
        font-size: 12px;
        &.module-action--secondary {
          color: var(--fts-secondary);
        }
        &:not(:first-child) {
          margin-left: 6px;
        }
      }
    }
    .module-toggle {
      margin-left: 0.5rem;
      font-size: 0.875rem;
      color: inherit;
      transition: all 0.3s;
    }
    &.module-group {
      &.open {
        .module-toggle {
          transform: rotate(90deg);
        }
      }
    }
    &:hover {
      background-color: rgba(var(--inistate-color-neutral-200-rgb), 0.5);
      > .module-info > .module-action-container {
        display: flex;
      }
    }
    &.active {
      background-color: rgba(var(--inistate-color-primary-200-rgb), 0.46);
    }
    &.module-wrapper--admin {
      color: var(--fts-secondary);
      &:hover,
      &.active {
        &::before {
          content: "";
          position: absolute;
          inset: 0;
          background-color: var(--fts-card-background);
          border-radius: inherit;
          z-index: -1;
        }
      }
      &:hover {
        background-color: rgba(var(--fts-secondary-rgb), 0.09);
      }
      &.active {
        background-color: rgba(var(--fts-secondary-rgb), 0.18);
      }
    }
  }
}
</style>